import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StudentDetailDto } from '@motoschool/shared';
import {
  formatDuration,
  formatMoney,
  HOUR_ENTRY_REASON_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
} from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageLoader,
  SectionTitle,
  Sheet,
  Textarea,
} from '../components/ui';
import { LoginLinkCard } from '../components/LoginLinkCard';
import { SlotCard } from '../components/SlotCard';
import { formatDateTime } from '../lib/format';

export function StudentDetailPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const { timeZone } = useAuth();
  const queryClient = useQueryClient();

  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState({ amount: '', hours: '10', comment: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['students', id, 'detail'],
    queryFn: () => api.get<StudentDetailDto>(`/users/${id}/detail`),
    enabled: Boolean(id),
  });

  const addPayment = useMutation({
    mutationFn: () =>
      api.post('/payments', {
        studentId: id,
        amountMinor: Math.round(Number(payment.amount.replace(',', '.')) * 100),
        minutesAdded: Math.round(Number(payment.hours.replace(',', '.')) * 60),
        // Способ оплаты не спрашиваем: деньги принимает инструктор наличными,
        // а онлайн-оплату проводит провайдер, а не эта форма.
        status: 'SUCCEEDED',
        comment: payment.comment || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['students', id] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsPaying(false);
      setPayment({ amount: '', hours: '10', comment: '' });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось внести оплату');
    },
  });

  if (isLoading) return <PageLoader />;
  if (!data) return <EmptyState title="Ученик не найден" />;

  const balance = data.balanceMinutes;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Avatar name={data.student.name} />
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold">{data.student.name}</h1>
          <p className="truncate text-sm text-muted">
            {data.student.phone ?? data.student.email ?? 'Контакты не указаны'}
          </p>
        </div>
      </div>

      {/* Сводка: три числа, ради которых открывают карточку */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="p-3 text-center">
          <p className="text-xs text-muted">Баланс</p>
          <p
            className={`mt-1 text-base font-semibold tabular-nums ${
              balance < 0 ? 'text-danger' : 'text-ink'
            }`}
          >
            {formatDuration(balance)}
          </p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-xs text-muted">Оплачено</p>
          <p className="mt-1 text-base font-semibold tabular-nums">
            {formatMoney(data.paidMinor, data.currency)}
          </p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-xs text-muted">Занятий</p>
          <p className="mt-1 text-base font-semibold tabular-nums">{data.lessonsCompleted}</p>
        </Card>
      </div>

      {/* Сколько часов из оплаченных уже израсходовано */}
      <Card>
        <div className="flex items-baseline justify-between">
          <p className="text-sm text-muted">Отзанимался</p>
          <p className="text-sm font-medium tabular-nums">
            {formatDuration(data.spentMinutes)}
            <span className="text-muted"> / {formatDuration(data.creditedMinutes)}</span>
          </p>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-line">
          <div
            className={`h-full rounded-full ${
              data.spentMinutes > data.creditedMinutes ? 'bg-danger' : 'bg-ink'
            }`}
            style={{
              width: `${
                data.creditedMinutes > 0
                  ? Math.min((data.spentMinutes / data.creditedMinutes) * 100, 100)
                  : 0
              }%`,
            }}
          />
        </div>
      </Card>

      <Button
        fullWidth
        onClick={() => {
          setError(null);
          setIsPaying(true);
        }}
      >
        Внести оплату
      </Button>

      <LoginLinkCard userId={id!} subject="Ученик" />

      {data.upcomingSlots.length > 0 && (
        <section>
          <SectionTitle>Ближайшие занятия</SectionTitle>
          <div className="space-y-2">
            {data.upcomingSlots.slice(0, 5).map((slot) => (
              <SlotCard key={slot.id} slot={slot} timeZone={timeZone} hideStudent />
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionTitle>Оплаты</SectionTitle>
        {data.payments.length === 0 ? (
          <EmptyState title="Оплат ещё не было" />
        ) : (
          <div className="space-y-2">
            {data.payments.map((item) => (
              <Card key={item.id} className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium tabular-nums">
                      {formatMoney(item.amountMinor, item.currency)}
                    </p>
                    <p className="text-xs text-muted">
                      {PAYMENT_METHOD_LABELS[item.method]} ·{' '}
                      {formatDateTime(item.paidAt ?? item.createdAt, timeZone)}
                    </p>
                    {item.comment && <p className="mt-1 text-sm text-muted">{item.comment}</p>}
                  </div>
                  <div className="text-right">
                    <Badge tone={item.status === 'SUCCEEDED' ? 'success' : 'neutral'}>
                      {PAYMENT_STATUS_LABELS[item.status]}
                    </Badge>
                    <p className="mt-1 text-xs text-muted tabular-nums">
                      +{formatDuration(item.minutesAdded)}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionTitle>История часов</SectionTitle>
        {data.hourEntries.length === 0 ? (
          <EmptyState title="Движений по часам ещё нет" />
        ) : (
          <Card className="divide-y divide-line p-0">
            {data.hourEntries.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{HOUR_ENTRY_REASON_LABELS[entry.reason]}</p>
                  <p className="text-xs text-muted">
                    {formatDateTime(entry.createdAt, timeZone)}
                    {entry.comment ? ` · ${entry.comment}` : ''}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-sm font-semibold tabular-nums ${
                    entry.deltaMinutes < 0 ? 'text-danger' : 'text-success'
                  }`}
                >
                  {entry.deltaMinutes > 0 ? '+' : ''}
                  {formatDuration(entry.deltaMinutes)}
                </span>
              </div>
            ))}
          </Card>
        )}
      </section>

      <Sheet
        isOpen={isPaying}
        title="Оплата занятий"
        onClose={() => setIsPaying(false)}
        footer={
          <div className="flex gap-2 pb-1">
            <Button variant="secondary" fullWidth onClick={() => setIsPaying(false)}>
              Отмена
            </Button>
            <Button fullWidth isLoading={addPayment.isPending} onClick={() => addPayment.mutate()}>
              Внести
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && <ErrorNotice message={error} />}

          <Field label="Сумма" required>
            <Input
              value={payment.amount}
              onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
              inputMode="decimal"
              placeholder="25000"
            />
          </Field>

          <Field label="Сколько часов начислить" required hint="Списываются по мере проведения занятий">
            <Input
              value={payment.hours}
              onChange={(event) => setPayment({ ...payment, hours: event.target.value })}
              inputMode="decimal"
            />
          </Field>

          <Field label="Комментарий">
            <Textarea
              value={payment.comment}
              onChange={(event) => setPayment({ ...payment, comment: event.target.value })}
              placeholder="Пакет из 10 занятий"
            />
          </Field>
        </div>
      </Sheet>
    </div>
  );
}
